import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';

import { CONTRIBUTOR_PERMISSION_CHECK_TYPE } from '../../../../../constants/contributors';
import { ContributorPermissions } from './graphql/queries';

/**
 * @typedef {Object} PermissionItem
 * @property {string} type - The permission type.
 * @property {string} permission - The actual permission.
 */

/**
 * Determines whether an user has certain permissions based on their
 * contributor hierarchy.
 *
 * @param {Object} options The option list.
 * @param {String} options.entityId ID of the entity (Album or AlbumFolder) to check permission for.
 * @param {Array<PermissionItem>} options.permissions List of PermissionItems to check for.
 * @param {String} options.checkType How to deal with provided permissions. Either Inclusive or exclusive.
 * @param {Boolean} options.isOwner Allows to bypass entire check by assuming the user owns the content.
 */
const useContributorPermission = ({
  entityId,
  permissions = [],
  checkType = CONTRIBUTOR_PERMISSION_CHECK_TYPE.ANY,
  isOwner = false,
} = {}) => {
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { loading, data: { me, hierarchyItem } = {} } = useQuery(
    ContributorPermissions,
    {
      variables: {
        id: entityId,
      },
      notifyOnNetworkStatusChange: true,
      skip: !entityId || isOwner || !authenticated,
    },
  );

  if (isOwner) {
    return {
      isContributor: false,
      isAllowed: true,
      isOwner,
    };
  }

  const isCreator =
    hierarchyItem?.album?.creator.id === me?.id ||
    hierarchyItem?.album_folder?.creator.id === me?.id;

  if (!entityId || !authenticated) {
    return {
      isContributor: false,
      isAllowed: false,
      loadingPermissions: loading,
      isOwner: isCreator,
    };
  }

  if (loading) {
    return {
      // Assume a user has no permission until proven otherwise.
      isContributor: true,
      isAllowed: false,
      loadingPermissions: true,
      isOwner: isCreator,
    };
  }

  if (isCreator) {
    return {
      isContributor: false,
      isOwner: isCreator,
      isAllowed: true,
    };
  }

  const contributorHierarchyRecord = me?.contributor_hierarchy_items.records[0];

  if (
    checkType === CONTRIBUTOR_PERMISSION_CHECK_TYPE.NO_CONTRIBUTOR &&
    contributorHierarchyRecord
  ) {
    // Permission to be created or not allowed at all
    return {
      isContributor: true,
      isAllowed: false,
      contributorHierarchyRecord,
      isOwner: isCreator,
    };
  }

  if (!permissions.length) {
    // Allow access if no ownership is required. Otherwise, ensure we check for it.
    return {
      isContributor: !!contributorHierarchyRecord || !isCreator,
      isAllowed: isCreator,
      contributorHierarchyRecord,
      contributorAssignment: contributorHierarchyRecord?.contributor_assignment,
      isOwner: isCreator,
    };
  }

  if (!contributorHierarchyRecord) {
    // At this point, since no hierarchy record is found, we are good to assume that the user
    // is only allowed if they're the creator, but they're certainly not a contributor
    return {
      isContributor: !isCreator,
      isAllowed: isCreator,
      contributorHierarchyRecord,
      isOwner: isCreator,
    };
  }

  const contributorAssignment =
    contributorHierarchyRecord.contributor_assignment;

  if (checkType === CONTRIBUTOR_PERMISSION_CHECK_TYPE.ANY) {
    return {
      isContributor: !!contributorHierarchyRecord || !isCreator,
      isAllowed: !!permissions.find(
        permissionItem =>
          permissionItem.permission ===
          contributorAssignment[permissionItem.type],
      ),
      contributorAssignment,
      contributorHierarchyRecord,
      isOwner: isCreator,
    };
  }

  // CONTRIBUTOR_PERMISSION_CHECK_TYPE: ALL
  return {
    isContributor: !!contributorHierarchyRecord || !isCreator,
    isAllowed: !permissions.find(
      permissionItem =>
        permissionItem.permission !==
        contributorAssignment[permissionItem.type],
    ),
    contributorAssignment,
    contributorHierarchyRecord,
    isOwner: isCreator,
  };
};

export default useContributorPermission;
